import { expected, randomUUID } from '@aloft/toolkit-shared';
import { Image, Text, View } from '@react-pdf/renderer';

import { getCommentaryTableHelperText } from '../../../redux/features/selectors/utils/getCommentaryTableHelperText.ts';
import { COMMENTARY_TEXT, PNG_HEIGHT, PNG_WIDTH } from '../../commentary/consts.ts';
import { ExportChartContainer } from '../../commentary/export/ExportChartContainer.tsx';
import { CommentarySection } from '../components/CommentarySection.tsx';
import { Table } from '../components/Table.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles } from '../styles.ts';
import { DateOfSaleAdjustmentsPageProps } from '../types.ts';

const AffectedCompCell = ({
  affectedCompsRaw,
  compSequence,
  compTypes,
}: {
  affectedCompsRaw: string;
  compSequence: DateOfSaleAdjustmentsPageProps['compSequence'];
  compTypes: DateOfSaleAdjustmentsPageProps['compTypes'];
}) => {
  if (affectedCompsRaw === '') return <Table.Cell compact style={{ flexGrow: 1 }} />;
  if (affectedCompsRaw === 'Comps Affected') {
    return (
      <Table.TextCell compact style={{ flexGrow: 1 }}>
        {affectedCompsRaw}
      </Table.TextCell>
    );
  }

  const affectedComps = affectedCompsRaw
    .split(',')
    .map((val) => Number(val.trim().replace('C', '')));

  return (
    <Table.Cell compact style={[{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }]}>
      {affectedComps.map((affectedComp) => {
        const compId = expected(compSequence[affectedComp - 1]);
        const compType = compTypes[compId];

        return (
          <Table.CompTypeCell key={randomUUID()} compact compType={compType}>
            {affectedComp}
          </Table.CompTypeCell>
        );
      })}
    </Table.Cell>
  );
};

export const DateOfSaleAdjustmentsPage = ({
  subjectAddress,
  adjustmentSettings,
  adjustmentsTableData,
  tkv2ChartImages: { dateOfSaleChartImage },
  compSequence,
  compTypes,
}: DateOfSaleAdjustmentsPageProps) => {
  const marketAdjustmentsTableData = adjustmentsTableData.marketAdjustments;
  const { method: listToSaleMethod } = adjustmentsTableData.listToSale ?? {};

  if (!marketAdjustmentsTableData || !listToSaleMethod) return null;

  const { commentary, method, tableData } = marketAdjustmentsTableData;

  const { title, subtitle } = COMMENTARY_TEXT['date-of-sale-adjustments'];
  const helperText = getCommentaryTableHelperText(adjustmentSettings, 'dateOfSale');

  return (
    <Tkv2PDFPage title={title} address={subjectAddress}>
      <Text style={fontStyles.body}>{subtitle}</Text>

      {dateOfSaleChartImage && (
        <ExportChartContainer
          title={method === 'zillow' ? 'Zillow Home Value Index' : 'Custom Home Value Index'}
        >
          <Image src={dateOfSaleChartImage} style={{ height: PNG_HEIGHT, width: PNG_WIDTH }} />
        </ExportChartContainer>
      )}

      <View style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
        {tableData.map((table) => (
          <Table key={randomUUID()}>
            {table[0]!.map((headerCell, columnIndex) => (
              <Table.Column key={randomUUID()}>
                <Table.TextCell compact>{headerCell.value}</Table.TextCell>
                <Table.TextCell compact>{table[1]![columnIndex]!.value}</Table.TextCell>
                <Table.TextCell compact>{table[2]![columnIndex]!.value}</Table.TextCell>
                <AffectedCompCell
                  affectedCompsRaw={table[3]![columnIndex]!.value}
                  compSequence={compSequence}
                  compTypes={compTypes}
                />
              </Table.Column>
            ))}
          </Table>
        ))}
      </View>

      {helperText && <Text style={fontStyles.small}>{helperText}</Text>}
      <CommentarySection title="Date of Sale Commentary" commentary={commentary} />
    </Tkv2PDFPage>
  );
};
